import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PHeading, PText, PTable, PTableBody } from "@porsche-design-system/components-react"
import { spacingStaticLg } from "@porsche-design-system/components-react/emotion"
import type {
	InventoryListItem,
	InventoryListSortingKey,
	InventoryListView,
	SortingValue,
} from "@slfinrtl/ui-bff-routes"
import type { ReactNode } from "react"
import { useCallback, useMemo } from "react"
import { FormattedMessage } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import type { TableSortingButtonProps } from "src/components"
import { InventoryListRow } from "./InventoryListRow"
import { InventoryListTableHead } from "./InventoryListTableHead"
import type { InventoryListTableHeadProps } from "./InventoryListTableHead"
import { messages } from "./inventoryList.messages"

interface InventoryListProps {
	currentVehiclesSelectionState?: boolean | "indeterminate"
	displayLocation: boolean
	isCarSalesLiteEnabled: boolean
	isFirstRegistrationDateAvailable: boolean
	onAddPriceClick: () => void
	onAddStockNumberClick: () => void
	onCurrentVehiclesSelectionToggle?: () => void
	onSortingChange?: (key: InventoryListSortingKey) => void
	onVehicleSelectionToggle: (vin: string) => void
	renderActionMenu: (item: InventoryListItem) => ReactNode
	selectedVehicles: Set<string>
	sorting?: SortingValue<InventoryListSortingKey>
	view: InventoryListView
}

export const InventoryList = (props: InventoryListProps) => {
	const {
		currentVehiclesSelectionState,
		displayLocation,
		isCarSalesLiteEnabled,
		isFirstRegistrationDateAvailable,
		onAddPriceClick,
		onAddStockNumberClick,
		onCurrentVehiclesSelectionToggle,
		onSortingChange,
		onVehicleSelectionToggle,
		renderActionMenu,
		selectedVehicles,
		sorting,
		view,
	} = props
	const { items, total } = view
	const { locale } = useDealerContext()

	const getSortDirection = useCallback(
		(key: string): TableSortingButtonProps["direction"] => {
			if (sorting?.sortBy !== key) {
				return undefined
			}

			return sorting.sortDir
		},
		[sorting],
	)

	const handleSortingChange = useCallback(
		(key: InventoryListSortingKey) => {
			if (onSortingChange) {
				return () => onSortingChange(key)
			}
		},
		[onSortingChange],
	)

	const getSort = useCallback(
		(key: InventoryListSortingKey): TableSortingButtonProps => ({
			direction: getSortDirection(key),
			onClick: handleSortingChange(key),
		}),
		[handleSortingChange, getSortDirection],
	)

	const tableHeadProps: InventoryListTableHeadProps = useMemo(
		() => ({
			currentVehiclesSelectionState,
			displayLocation,
			getSort,
			isCarSalesLiteEnabled,
			isFirstRegistrationDateAvailable,
			locale,
			onCurrentVehiclesSelectionToggle,
		}),
		[
			displayLocation,
			isCarSalesLiteEnabled,
			getSort,
			onCurrentVehiclesSelectionToggle,
			currentVehiclesSelectionState,
			isFirstRegistrationDateAvailable,
			locale,
		],
	)

	return (
		<div data-testid="inventory-list">
			<PHeading size="sm">
				<FormattedMessage {...commonMessages.vehicleCount} values={{ count: total }} />
			</PHeading>

			<PTable caption="Inventory List" layout="fixed" compact sticky>
				<InventoryListTableHead {...tableHeadProps} />
				<PTableBody>
					{items.map((item, index) => (
						<InventoryListRow
							key={item.id}
							actionMenu={renderActionMenu(item)}
							index={index}
							isCarSalesLiteEnabled={isCarSalesLiteEnabled}
							onVehicleSelectionToggle={onVehicleSelectionToggle}
							onAddPriceClick={onAddPriceClick}
							onAddStockNumberClick={onAddStockNumberClick}
							data-testid={`inventory-list-row_${index}`}
							displayLocation={displayLocation}
							isFirstRegistrationDateAvailable={isFirstRegistrationDateAvailable}
							selectedVehicles={selectedVehicles}
							item={item}
						/>
					))}
				</PTableBody>
			</PTable>

			{!items.length && (
				<NoResults>
					<PHeading size="md">
						<FormattedMessage {...messages.noResultsHeadline} />
					</PHeading>
					<PText size="md">
						<FormattedMessage {...messages.noResultsSubline} />
					</PText>
				</NoResults>
			)}
		</div>
	)
}

const NoResults = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${spacingStaticLg} 0 0;
`
